/**
 * ROLE VIEWS — the single place that decides what each role sees.
 *
 * To change a view (or add one) edit ONLY this file:
 *   - nav:        which pages appear in the sidebar, in this order
 *   - landing:    where the user lands after sign-in
 *   - finance:    may this role see money figures (revenue, profit, rates)?
 *   - dashboard:  title + which widgets show on the home page (see WIDGETS in pages/Dashboard.jsx)
 *   - quickLinks: shortcut buttons for the "quick_links" widget
 *   - base:       CORE role whose SERVER permissions this view inherits (a finer view is
 *                 narrower in the UI, never more powerful on the server)
 *
 * Adding a brand-new view = (1) add "<role>": "<core role>" to ROLE_BASE in
 * backend/routes/auth.py, (2) add an entry below. Nothing else (Users page, role
 * validation and permissions pick it up automatically).
 *
 * NOTE: this controls what is SHOWN. The server (require_roles / finance-field
 * stripping) is what actually ENFORCES access.
 */

// Every page in the app: path -> label (icons are mapped in components/Layout.jsx)
export const PAGES = {
  "/": "Dashboard",
  "/daily": "Daily Dashboard",
  "/orders": "Orders / Jobs",
  "/production": "Production",
  "/avt": "AVT Inspection",
  "/inventory": "Inventory",
  "/machines": "Machines",
  "/equipment": "Equipment",
  "/operators": "Operators",
  "/sales": "Sales",
  "/purchases": "Purchases",
  "/customers": "Customers",
  "/reports": "Reports",
  "/users": "Users",
  "/import": "CSV Import",
  "/views": "Role Views",
};

const ALL_PAGES = Object.keys(PAGES);
// AVT Inspection is reached from the Production page; the admin's sidebar does not list it again.
const ADMIN_NAV = ALL_PAGES.filter((p) => p !== "/avt");

export const ROLE_VIEWS = {
  admin: {
    base: "admin",
    label: "Admin",
    summary: "Owner / IT. Sees and manages everything, including users and finance PINs.",
    landing: "/",
    finance: true,
    nav: ADMIN_NAV,
    dashboard: {
      subtitle: "Command Deck",
      title: "Dashboard",
      widgets: ["orders_search", "kpi_jobs", "kpi_dispatched", "kpi_to_be_planned", "kpi_revenue", "kpi_profit", "kpi_lowstock", "kpi_avg_invoice", "kpi_avg_job", "kpi_avg_label", "production_analytics", "calibration_due", "churn", "job_status", "trend", "recent_jobs", "low_stock"],
    },
  },

  production_manager: {
    base: "production_manager",
    label: "Production Manager",
    summary: "Runs the shop floor: jobs, machines, equipment, operators, stock. No money figures (revenue, cost, profit, rates).",
    landing: "/",
    finance: false,
    nav: ["/", "/daily", "/orders", "/production", "/avt", "/inventory", "/machines", "/equipment", "/operators", "/customers", "/reports", "/import"],
    dashboard: {
      subtitle: "Plant Overview",
      title: "Production Dashboard",
      widgets: ["kpi_jobs", "kpi_lowstock", "calibration_due", "churn", "job_status", "recent_jobs", "low_stock"],
    },
    quickLinks: [],
  },

  accounts: {
    base: "accounts",
    label: "Accounts",
    summary: "Billing, purchases, customers and money reports. Finance-first dashboard.",
    landing: "/",
    finance: true,
    nav: ["/", "/orders", "/inventory", "/sales", "/purchases", "/customers", "/reports", "/import"],
    dashboard: {
      subtitle: "Finance Desk",
      title: "Accounts Dashboard",
      widgets: ["kpi_jobs", "kpi_revenue", "kpi_profit", "kpi_avg_invoice", "kpi_avg_job", "kpi_avg_label", "churn", "job_status", "trend", "recent_jobs"],
    },
  },

  operator: {
    base: "operator",
    label: "Operator",
    summary: "Shop-floor staff. Jobs and production entry only. Never sees money.",
    landing: "/",
    finance: false,
    nav: ["/", "/orders", "/production"],
    dashboard: {
      subtitle: "My Shift",
      title: "Shop Floor",
      widgets: ["quick_links", "job_status", "recent_jobs"],
    },
    quickLinks: [
      { to: "/production", label: "Enter production", hint: "Quick Entry: log output, wastage, materials" },
      { to: "/orders", label: "Find a job", hint: "Open a job card, stages and BPR slip" },
    ],
  },

  hr: {
    base: "hr",
    label: "HR",
    summary: "People side only: operator roster and rates. No job, stock or finance data.",
    landing: "/",
    finance: false,
    nav: ["/", "/operators"],
    dashboard: {
      subtitle: "People",
      title: "HR Desk",
      widgets: ["quick_links", "operator_roster"],
    },
    quickLinks: [
      { to: "/operators", label: "Operator roster", hint: "Codes, skills, status, hourly rates" },
    ],
  },
  // ---------------- Finer views (each inherits a core role's server permissions) ----------------

  printing_operator: {
    base: "operator",
    label: "Printing",
    summary: "Printing desk: jobs ready to print, partially printed and already printed. No money.",
    landing: "/",
    finance: false,
    // No "/orders" link in the sidebar — Printing works from this dashboard's own job lists.
    // hiddenNav keeps the Orders Search widget's results reachable (for a job outside the default
    // lists) without putting "Orders / Jobs" back in the sidebar; a specific job's own page is always
    // reachable regardless (canAccess).
    nav: ["/"],
    hiddenNav: ["/orders"],
    dashboard: {
      subtitle: "Printing",
      title: "Printing Desk",
      widgets: [
        "orders_search",
        // Compact count tiles (same shape as every dashboard's "Partially dispatched" tile — which
        // Printing doesn't get, see Dashboard.jsx showPartialDispatch): click for the full list.
        // Scheduled Jobs (still in planning / BPR stage, hasn't reached Printing yet) is look-ahead
        // only — visible for awareness, never actually clickable into a job.
        "kpi_ready_to_print",
        "kpi_scheduled_jobs",
        { id: "jobs_by_status", status: "partially_printed", title: "Partially printed" },
      ],
    },
  },

  avt_operator: {
    base: "operator",
    label: "AVT Inspection",
    summary: "AVT desk: jobs ready for AVT, plus inspection analytics — labels passed vs rejected, by operator, machine and shift. No money.",
    landing: "/avt",
    finance: false,
    nav: ["/", "/avt", "/orders"],
    dashboard: {
      subtitle: "AVT",
      title: "AVT Inspection",
      widgets: [
        "orders_search",
        // Compact count tile (same shape as every dashboard's "Partially dispatched" tile, which AVT
        // doesn't get, see Dashboard.jsx showPartialDispatch), plus the full list card. AVT's
        // prerequisite is Printing — status "printing" (or "flat_bed" for jobs that went through
        // Flatbed) once it completes.
        "kpi_ready_for_inspection",
        { id: "jobs_by_status", status: "printing", title: "Ready for AVT" },
      ],
    },
  },

  slitting_operator: {
    base: "operator",
    label: "Slitting & Numbering",
    summary: "Slitting desk: jobs ready for Slitting & Numbering. No money.",
    landing: "/",
    finance: false,
    // No "/orders" link in the sidebar — Slitting works from this dashboard's own job lists.
    // hiddenNav keeps the Orders Search widget's results reachable without it; a specific job's
    // own page is always reachable regardless (canAccess).
    nav: ["/"],
    hiddenNav: ["/orders"],
    dashboard: {
      subtitle: "Slitting",
      title: "Slitting Desk",
      widgets: [
        "orders_search",
        // Compact count tile (same shape as every dashboard's "Partially dispatched" tile, which
        // Slitting doesn't get, see Dashboard.jsx showPartialDispatch), plus the full list card.
        // Slitting's prerequisite is AVT — status "avt" once it completes.
        "kpi_ready_for_slitting",
        { id: "jobs_by_status", status: "avt", title: "Ready for Slitting" },
      ],
    },
  },

  qc_inspector: {
    base: "operator",
    label: "QC Inspector",
    summary: "Quality desk. Sees jobs waiting for QC, jobs ready for their COA certificate, and received materials waiting for incoming QC (approves or rejects them). No money.",
    landing: "/",
    finance: false,
    // No "/orders" link in the sidebar — QC works from this dashboard's own job lists. hiddenNav
    // keeps the Orders Search widget's results reachable without putting "Orders / Jobs" back in
    // the sidebar; a specific job's own page is always reachable regardless (canAccess).
    nav: ["/"],
    hiddenNav: ["/orders"],
    dashboard: {
      subtitle: "Quality",
      title: "QC Desk",
      widgets: [
        "orders_search",
        // Compact count tiles (same shape as every dashboard's "Partially dispatched" tile): click
        // for the full list — QC owns both Quality and COA, so both are fully actionable here.
        // Raw materials waiting for QC opens its list in its own dialog (kpi_materials_qc), not as
        // an always-visible section.
        "kpi_materials_qc",
        "kpi_jobs_ready_for_qc",
        "kpi_ready_for_coa",
        "job_status",
      ],
    },
  },

  packing_operator: {
    base: "operator",
    label: "Packing",
    summary: "Packing desk: jobs ready to pack (the Med Pack register). No money.",
    landing: "/",
    finance: false,
    nav: ["/", "/orders"],
    dashboard: {
      subtitle: "Packing",
      title: "Packing Desk",
      widgets: [
        "orders_search",
        // Compact count tile (same shape as every dashboard's "Partially dispatched" tile, which
        // Packing doesn't get, see Dashboard.jsx showPartialDispatch), plus the full list card.
        // Packing's prerequisite is Quality — status "qc" is set once Quality completes, i.e. now
        // waiting to be packed.
        "kpi_ready_for_packing",
        { id: "jobs_by_status", status: "qc", title: "Ready for packing" },
      ],
    },
  },

  dispatch_clerk: {
    base: "operator",
    label: "Dispatch",
    summary: "Dispatch. Sees jobs ready to dispatch. No money.",
    landing: "/",
    finance: false,
    nav: ["/", "/orders"],
    dashboard: {
      subtitle: "Dispatch",
      title: "Dispatch Desk",
      widgets: [
        "orders_search",
        // Compact count tiles (same shape as every department's), alongside Partially Dispatched —
        // Dispatch keeps that one too, dispatch status is literally their own desk's concern.
        "kpi_ready_to_dispatch",
        "kpi_jobs_dispatched",
      ],
    },
    quickLinks: [],
  },

  stores_keeper: {
    base: "production_manager",
    label: "Stores Keeper",
    summary: "Raw-material stores: goods receipt, incoming QC, issue to BPR, returns, slitting and stock reports. Server rights = production manager (see risks).",
    landing: "/inventory",
    finance: false,
    nav: ["/", "/inventory", "/import"],
    dashboard: {
      subtitle: "Stores",
      title: "Stores Desk",
      widgets: ["quick_links", "stores_dashboard"],
    },
    quickLinks: [
      { to: "/inventory?tab=jobs", label: "Jobs waiting for material", hint: "BPRs to issue, most urgent first" },
      { to: "/inventory?tab=movements&type=receipt", label: "Goods receipt", hint: "Receive rolls against a GRN, then QC" },
    ],
  },

  shift_supervisor: {
    base: "production_manager",
    label: "Shift Supervisor",
    summary: "Runs a shift: jobs, production entry, operators, machines. No customers / reports / money.",
    landing: "/",
    finance: false,
    nav: ["/", "/orders", "/production", "/operators", "/machines", "/equipment"],
    dashboard: {
      subtitle: "Shift",
      title: "Shift Board",
      widgets: ["quick_links", "kpi_jobs", "job_status", { id: "jobs_by_status", status: "in_production", title: "Running now" }],
    },
    quickLinks: [
      { to: "/production", label: "Enter production", hint: "Quick Entry" },
      { to: "/machines", label: "Machines", hint: "Status and performance" },
    ],
  },

  billing_clerk: {
    base: "accounts",
    label: "Billing Clerk",
    summary: "Invoices and customers. Sales and customer pages only; no purchases or P&L.",
    landing: "/sales",
    finance: true,
    nav: ["/", "/orders", "/sales", "/customers"],
    dashboard: {
      subtitle: "Billing",
      title: "Billing Desk",
      widgets: ["kpi_jobs", "kpi_revenue", "recent_jobs"],
    },
  },

  purchase_officer: {
    base: "accounts",
    label: "Purchase Officer",
    summary: "Buying. Purchases and inventory; no sales or customer data.",
    landing: "/purchases",
    finance: true,
    nav: ["/", "/purchases", "/inventory"],
    dashboard: {
      subtitle: "Purchasing",
      title: "Purchase Desk",
      widgets: ["kpi_lowstock", "low_stock", "quick_links"],
    },
    quickLinks: [{ to: "/purchases", label: "Purchases", hint: "POs and supplier bills" }],
  },

  // View-only, full-visibility role for scheduling: every job, no department restriction (not in
  // job_status.py's ROLE_STAGES, so the server leaves it unrestricted), but no money and no bulk
  // actions (base "operator" keeps canBulk/canCreate off in Orders.jsx). Dashboard + All Jobs only.
  operations_head: {
    base: "operator",
    label: "Operations Head",
    summary: "Sees every job across all departments for scheduling. Dashboard and All Jobs only — no money, no bulk actions.",
    landing: "/",
    finance: false,
    nav: ["/", "/orders"],
    navLabels: { "/orders": "Scheduling Jobs" },
    dashboard: {
      subtitle: "Operations",
      title: "Operations Head",
      widgets: [
        "orders_search", "kpi_jobs", "kpi_to_be_planned",
        "kpi_dept_stores", "kpi_dept_printing", "kpi_dept_avt", "kpi_dept_slitting",
        "kpi_dept_quality", "kpi_dept_packing", "kpi_dept_dispatch",
      ],
    },
  },
};

export const ROLE_NAMES = Object.keys(ROLE_VIEWS);

/** Core role whose permissions a user inherits (matches backend ROLE_BASE). */
export function baseRoleOf(role) {
  return (ROLE_VIEWS[role] && ROLE_VIEWS[role].base) || role;
}

export function viewFor(user) {
  return (user && ROLE_VIEWS[user.role]) || ROLE_VIEWS.operator; // unknown role -> least privilege
}

/** Can this user open this path? Admin always; others if listed in their nav OR their hiddenNav (a
 * path that's reachable — e.g. the Orders Search widget's results — without appearing as its own
 * sidebar link) — except a specific job's own detail page (/orders/:id), always reachable even when
 * "/orders" (the browse-all list) is in neither, since a dashboard widget link still needs to open it
 * and the backend's own per-job check (routes/jobs.py get_job) is what actually gates whether THIS job
 * may be opened. */
export function canAccess(user, path) {
  if (!user) return false;
  if (user.role === "admin") return true;
  const segs = path.split("/").filter(Boolean);
  if (segs[0] === "orders" && segs.length > 1) return true;
  if (segs[0] === "s" && segs.length > 1) return true;  // a scanned sheet: the server decides what it shows
  const base = "/" + (segs[0] || "");
  const view = viewFor(user);
  return view.nav.includes(base === "/" ? "/" : base) || (view.hiddenNav || []).includes(base);
}
